import React, { useCallback, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import incruiterLogo from "../../../assets/images/incruiter-main-logo.svg";
import candidate from "../../../utils/api/candidate";
import { useToast } from "../../hooks/useToast";

const EXPERIENCE_OPTIONS = [
  { key: "very_poor", emoji: "😩", label: "Very Poor" },
  { key: "poor", emoji: "😕", label: "Poor" },
  { key: "average", emoji: "😐", label: "Average" },
  { key: "good", emoji: "🙂", label: "Good" },
  { key: "excellent", emoji: "😄", label: "Excellent" },
];

const CLARITY_OPTIONS = [
  { key: "very_unclear", emoji: "😩", label: "Very Unclear" },
  { key: "unclear", emoji: "😕", label: "Unclear" },
  { key: "neutral", emoji: "😐", label: "Neutral" },
  { key: "clear", emoji: "🙂", label: "Clear" },
  { key: "very_clear", emoji: "😄", label: "Very Clear" },
];

const AI_VS_HUMAN_OPTIONS = ["Better", "Similar", "Worse"] as const;

const EXPERIENCE_TO_NUMBER: Record<string, number> = {
  very_poor: 1,
  poor: 2,
  average: 3,
  good: 4,
  excellent: 5,
};

const CLARITY_TO_NUMBER: Record<string, number> = {
  very_unclear: 1,
  unclear: 2,
  neutral: 3,
  clear: 4,
  very_clear: 5,
};

interface EmojiOptionProps {
  emoji: string;
  label: string;
  selected: boolean;
  onClick: () => void;
}

const EmojiOption: React.FC<EmojiOptionProps> = ({ emoji, label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-col items-center gap-1 sm:gap-1.5 px-1 sm:px-2 py-2 sm:py-2.5 rounded-xl border-2 transition-all cursor-pointer flex-1 min-w-0 ${
      selected
        ? "border-[#0EA5E9] bg-[#E0F2FE]"
        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
    }`}
  >
    <span className="text-lg sm:text-2xl">{emoji}</span>
    <span
      className={`text-[9px] sm:text-xs font-medium text-center leading-tight ${
        selected ? "text-[#0EA5E9]" : "text-gray-600"
      }`}
    >
      {label}
    </span>
  </button>
);

export default function FeedbackCSAT() {
  const { toast } = useToast();
  const { interviewId = "", roomId: rawRoomId = "" } = useParams();
  const location = useLocation();
  const roomId = rawRoomId.replace(/[?&]rating=\d+.*$/, "");

  const NUMBER_TO_EXPERIENCE: Record<string, string> = {
    "1": "very_poor",
    "2": "poor",
    "3": "average",
    "4": "good",
    "5": "excellent",
  };

  const ratingParam =
    new URLSearchParams(location.search).get("rating") ||
    rawRoomId.match(/[?&]rating=(\d+)/)?.[1] ||
    "";
  const initialExperience = NUMBER_TO_EXPERIENCE[ratingParam] ?? null;

  const [overallExperience, setOverallExperience] = useState<string | null>(initialExperience);
  const [questionClarity, setQuestionClarity] = useState<string | null>(null);
  const [aiVsHuman, setAiVsHuman] = useState<string | null>(null);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = overallExperience !== null && questionClarity !== null && aiVsHuman !== null;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    const payload = {
      answers: [
        {
          question: "overallInterviewExperience",
          answer: EXPERIENCE_TO_NUMBER[overallExperience!] ?? 0,
          comments: "",
        },
        {
          question: "questionClarity",
          answer: CLARITY_TO_NUMBER[questionClarity!] ?? 0,
          comments: "",
        },
        {
          question: "aiVsHumanComparison",
          answer: aiVsHuman,
          comments: "",
        },
        {
          question: "comment",
          answer: comments.trim(),
        },
      ],
      productName: "IncScreen",
      interviewId,
      roomId,
    };

    setSubmitting(true);
    try {
      const { data } = await candidate.post("/interviews/csat/feedback/submit", payload, {
        headers: { "Content-Type": "application/json" },
      });
      const message = data?.result?.message || "Feedback submitted.";
      toast({ title: "Success", description: message, variant: "success" });
      setSubmitted(true);
    } catch (err: any) {
      const message =
        err?.response?.data?.message || "Failed to submit feedback. Please try again.";
      console.log("message:", message);
      toast({ title: "Error", description: message, variant: "error" });
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, overallExperience, questionClarity, aiVsHuman, comments, interviewId]);

  if (submitted) {
    return (
      <div className="min-h-screen md:h-screen md:overflow-hidden bg-[#F1F5F9] flex flex-col font-manrope">
        {/* Header */}
        <div className="flex justify-center px-3 sm:px-4 pt-3 shrink-0">
          <header className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full max-w-2xl py-3 sm:py-4 flex justify-center">
            <img src={incruiterLogo} alt="InCruiter" className="h-10 sm:h-14 object-contain" />
          </header>
        </div>

        <div className="flex-1 min-h-0 flex flex-col items-center px-3 sm:px-4 pt-2 pb-5">
          <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 w-full max-w-2xl px-5 sm:px-7 py-8 sm:py-12 text-center flex flex-col items-center justify-center">
            {/* Check icon */}
            <div className="flex justify-center mb-4 sm:mb-5">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#E0F7F5] flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path
                    d="M7 16.5L13 22.5L25 10"
                    stroke="#2BBFB3"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Thank you for your feedback!
            </h2>
            <p className="text-gray-400 text-xs sm:text-sm mb-6">
              Your response helps us improve the candidate experience at InCruiter.
            </p>

            {/* Review on Google */}
            <p className="text-sm font-semibold text-gray-800 mb-1">Review us on</p>
            <div className="flex justify-center mb-2">
              <span className="text-3xl sm:text-4xl font-bold tracking-tight">
                <span style={{ color: "#4285F4" }}>G</span>
                <span style={{ color: "#EA4335" }}>o</span>
                <span style={{ color: "#FBBC05" }}>o</span>
                <span style={{ color: "#4285F4" }}>g</span>
                <span style={{ color: "#34A853" }}>l</span>
                <span style={{ color: "#EA4335" }}>e</span>
              </span>
            </div>
            <div className="flex justify-center gap-1 mb-5 text-xl sm:text-2xl text-yellow-400">
              {"★★★★★".split("").map((s, i) => (
                <span key={i}>{s}</span>
              ))}
            </div>

            <a
              href="https://share.google/DFX8zOeSXCYRYUitT"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 rounded-xl border-2 border-[#2BBFB3] text-[#2BBFB3] text-sm font-semibold hover:bg-[#E0F7F5] transition-colors"
            >
              {/* Google G icon */}
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                  fill="#4285F4"
                />
                <path
                  d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
                  fill="#34A853"
                />
                <path
                  d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.826.957 4.039l3.007-2.332z"
                  fill="#FBBC05"
                />
                <path
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 6.293C4.672 4.165 6.656 3.58 9 3.58z"
                  fill="#EA4335"
                />
              </svg>
              Leave Google Review
            </a>
          </div>
        </div>

        <footer className="py-3 text-center text-xs text-gray-500 shrink-0">
          <span className="font-semibold text-gray-700">InCruiter</span> • AI Powered Interview
          Platform
          <br />© {new Date().getFullYear()} InCruiter. All rights reserved.
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col font-manrope">
      {/* Header */}
      <div className="flex justify-center px-3 sm:px-4 pt-3 shrink-0">
        <header className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full max-w-2xl py-3 sm:py-4 flex justify-center">
          <img src={incruiterLogo} alt="InCruiter" className="h-10 sm:h-14 object-contain" />
        </header>
      </div>

      {/* Main */}
      <div className="flex-1 min-h-0 flex flex-col items-center px-3 sm:px-4 pt-2 pb-4 overflow-y-auto">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-sm border border-gray-200 px-4 sm:px-7 py-5 sm:py-6 flex flex-col gap-4 sm:gap-5 my-2">
          {/* Title */}
          <div className="text-center">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-0.5">
              How was your Interview Experience?
            </h1>
            <p className="text-xs sm:text-sm text-gray-400">
              Your feedback helps us improve candidate experience
            </p>
          </div>

          {/* Q1 — Overall experience */}
          <div>
            <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
              How was your overall experience?
            </p>
            <div className="flex gap-1.5 sm:gap-2">
              {EXPERIENCE_OPTIONS.map((opt) => (
                <EmojiOption
                  key={opt.key}
                  emoji={opt.emoji}
                  label={opt.label}
                  selected={overallExperience === opt.key}
                  onClick={() => setOverallExperience(opt.key)}
                />
              ))}
            </div>
          </div>

          {/* Q2 — Question clarity */}
          <div>
            <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
              Were the questions asked by the AI recruiter clear and easy to understand?
            </p>
            <div className="flex gap-1.5 sm:gap-2">
              {CLARITY_OPTIONS.map((opt) => (
                <EmojiOption
                  key={opt.key}
                  emoji={opt.emoji}
                  label={opt.label}
                  selected={questionClarity === opt.key}
                  onClick={() => setQuestionClarity(opt.key)}
                />
              ))}
            </div>
          </div>

          {/* Q3 — AI vs Human */}
          <div>
            <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
              Was your experience with the AI interview better than, similar to, or worse than a
              human-led interview?
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {AI_VS_HUMAN_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setAiVsHuman(option)}
                  className={`px-5 sm:px-6 py-1.5 rounded-full border-2 text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                    aiVsHuman === option
                      ? "border-[#0EA5E9] bg-[#E0F2FE] text-[#0EA5E9]"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Comments */}
          <div>
            <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">
              Additional Comments (Optional)
            </p>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value.slice(0, 500))}
              placeholder="Share your experience..."
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-2.5 text-xs sm:text-sm text-gray-700 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
            />
          </div>

          {/* Submit */}
          <button
            type="button"
            disabled={!canSubmit || submitting}
            onClick={handleSubmit}
            className={`w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-all ${
              canSubmit && !submitting
                ? "bg-[#4DD8CF] hover:bg-[#3BC9C0] cursor-pointer"
                : "bg-[#A7E8E4] cursor-not-allowed"
            }`}
          >
            {submitting ? "Submitting..." : "Submit Feedback"}
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-3 text-center text-xs text-gray-500 shrink-0">
        <span className="font-semibold text-gray-700">InCruiter</span> • AI Powered Interview
        Platform
        <br />© {new Date().getFullYear()} InCruiter. All rights reserved.
      </footer>
    </div>
  );
}
